import { useQuery, useQueries } from '@tanstack/react-query';

import { api } from '@/services/api';

const REFRESH_INTERVAL = 30000; // 30 seconds

export function useConfig() {
	return useQuery({
		queryKey: ['config'],
		queryFn: api.getConfig,
		staleTime: Infinity // Config doesn't change during session
	});
}

export function useBalance() {
	return useQuery({
		queryKey: ['balance'],
		queryFn: api.getBalance,
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});
}

export function usePositions() {
	return useQuery({
		queryKey: ['positions'],
		queryFn: api.getPositions,
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});
}

export function usePortfolio() {
	return useQuery({
		queryKey: ['portfolio'],
		queryFn: api.getPortfolio,
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});
}

export function useLatestData(symbol: string) {
	return useQuery({
		queryKey: ['latest', symbol],
		queryFn: () => api.getLatest(symbol),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000,
		enabled: !!symbol
	});
}

export function useHistory(symbol: string, limit = 100) {
	return useQuery({
		queryKey: ['history', symbol, limit],
		queryFn: () => api.getHistory(symbol, limit),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000,
		enabled: !!symbol
	});
}

export function useSignals(symbol: string, limit = 50, cursor?: string, types?: string[]) {
	return useQuery({
		queryKey: ['signals', symbol, limit, cursor, types],
		queryFn: () => api.getSignals(symbol, limit, cursor, types),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000,
		enabled: !!symbol
	});
}

export function useLatestSignal(symbol: string) {
	return useQuery({
		queryKey: ['latestSignal', symbol],
		queryFn: () => api.getLatestSignal(symbol),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000,
		enabled: !!symbol
	});
}

export function useLogs(limit = 50, cursor?: string, levels?: string[]) {
	return useQuery({
		queryKey: ['logs', limit, cursor, levels],
		queryFn: () => api.getLogs(limit, cursor, levels),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});
}

export function usePositionHistory(page = 1, limit = 10, symbol?: string) {
	return useQuery({
		queryKey: ['positionHistory', page, limit, symbol],
		queryFn: () => api.getPositionHistory(page, limit, symbol),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});
}

export function useOrders(page = 1, limit = 25, symbol?: string) {
	return useQuery({
		queryKey: ['orders', page, limit, symbol],
		queryFn: () => api.getOrders(page, limit, symbol),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});
}

// Fetch all position history for the last 30 days by paginating through results
export function useAllPositionHistory() {
	// First, get page 1 to check total pages
	const firstPage = useQuery({
		queryKey: ['positionHistory', 'all', 1, 1000],
		queryFn: () => api.getPositionHistory(1, 1000),
		refetchInterval: REFRESH_INTERVAL,
		staleTime: 25000
	});

	const totalPages = firstPage.data?.pagination?.totalPages || 1;

	// Fetch remaining pages
	const remainingPages = useQueries({
		queries:
			totalPages > 1
				? Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map((page) => ({
						queryKey: ['positionHistory', 'all', page, 1000],
						queryFn: () => api.getPositionHistory(page, 1000),
						refetchInterval: REFRESH_INTERVAL,
						staleTime: 25000,
						enabled: firstPage.isSuccess
					}))
				: []
	});

	// Combine all pages
	const allHistory = [
		...(firstPage.data?.history || []),
		...remainingPages.flatMap((q) => q.data?.history || [])
	];

	const isLoading = firstPage.isLoading || remainingPages.some((q) => q.isLoading);

	return {
		data: firstPage.data
			? {
					...firstPage.data,
					history: allHistory,
					pagination: {
						...firstPage.data.pagination,
						total: allHistory.length
					}
				}
			: undefined,
		isLoading,
		isError: firstPage.isError || remainingPages.some((q) => q.isError)
	};
}
